import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cleanDocument, formatCnpjString, hasCnpjShape } from "@/utils/validateCnpj";

export type DateInput = string | Date | null | undefined;

export const formatDelay = (delay?: number | null): string => {
  if (delay == null) return "-";
  return `D + ${delay}`;
};

export const formatPercentage = (value?: number | null): string => {
  if (value == null) return "-";
  return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}%`;
};

export const formatCurrency = (value?: number | null): string => {
  if (value == null) return "-";
  return `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
};

const parseDateInput = (date?: DateInput): Date | null => {
  if (!date) return null;

  if (date instanceof Date) return isValid(date) ? date : null;

  const normalized = date.includes("T") ? date : date.replace(" ", "T");
  const parsed = parseISO(normalized);

  return isValid(parsed) ? parsed : null;
};

export const formatDateToBr = (date?: DateInput): string => {
  const parsed = parseDateInput(date);
  return parsed ? format(parsed, "dd/MM/yyyy") : "-";
};

export const formatTimeToBr = (date?: DateInput): string => {
  const parsed = parseDateInput(date);
  return parsed ? format(parsed, "HH:mm") : "-";
};

export const getInitialsFromEmail = (value?: string | null): string => {
  if (!value) return "";

  const localPart = value.split("@")[0] || "";
  const segments = localPart.split(/[._-]+/).filter(Boolean);

  if (segments.length >= 2) {
    return `${segments[0][0]}${segments[1][0]}`.toUpperCase();
  }

  return localPart.slice(0, 2).toUpperCase();
};

export const formatDateTimeToDisplay = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    return format(parseISO(value), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return null;
  }
};

export const formatDateOnlyToDisplay = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    return format(parseISO(value), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return null;
  }
};

export const formatMonthYearToDisplay = (value?: string | null): string => {
  if (!value) return '—';
  try {
    const label = format(parseISO(value), 'MMMM yyyy', { locale: ptBR });
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return '—';
  }
};

export const getFirstWord = (name: string | null | undefined): string | null => {
  if (!name) return null;
  const word = name.trim().split(/\s+/)[0];
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const formatDocument = (document?: string | null): string => {
  if (!document) return "-";

  const cleanDoc = cleanDocument(document);

  if (/^\d{11}$/.test(cleanDoc)) {
    return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  if (hasCnpjShape(cleanDoc)) {
    return formatCnpjString(cleanDoc);
  }

  return document;
};

